import Column from '@amzn/meridian/column';
import Divider from '@amzn/meridian/divider';
import Row from '@amzn/meridian/row';
import React, { useContext } from 'react';

import { Profile } from '../../../Types';
import DataState from '../../../Types/DataState';
import { weeksList } from '../../../Util';
import ConditionalWrap from '../../../Util/ConditionalWrap';
import Coaches from '../DashboardComponents/Coaches';
import Profiles from '../DashboardComponents/Profiles';
import UserInfoSelect from '../DashboardComponents/UserInfoSelect';
import ContainerWrapper from './ContainerWrapper';
import ProxyWidget from '../DashboardComponents/ProxyWidget';
import { isFeatureEnabled, Feature } from '../../../config/FeaturesConfig';
import { RowWidths } from '@amzn/meridian/row/row';
import { StateContext } from '../../../GlobalStateStore';
import useProxy from '../../../hooks/useProxy';

interface ProfileContainerProps {
  state: string;
  profile: Profile;
  selectedWeek: string;
  setWeek?: (e: React.FormEvent<HTMLSelectElement>) => void;
  isBig: boolean;
}

const ProfileContainer: React.FC<ProfileContainerProps> = ({
  state,
  profile,
  selectedWeek,
  isBig,
}: ProfileContainerProps): React.ReactElement => {
  const teamProxyEnabled = isFeatureEnabled(Feature.TEAM_PROXY);
  const { isProxying } = useProxy();

  const globalState = useContext(StateContext);
  const { isTm } = globalState.state.inspireOptions.data;

  const showProxyWidget = (
    isTm: boolean,
    profile: Profile,
    isProxying: boolean
  ): boolean => {
    // Always show proxy widget if user is currently in a proxy session (for exit capability)
    if (isProxying) {
      return teamProxyEnabled;
    }

    // Normal condition: show for TMs with direct reports
    return teamProxyEnabled && isTm && profile.directReports.length > 0;
  };

  const getRowWidths = (
    isBig: boolean,
    showProxyWidget: boolean
  ): RowWidths => {
    if (isBig) {
      return showProxyWidget
        ? ['grid-4', 'grid-2', 'grid-3', 'grid-3']
        : ['grid-6', 'grid-3', 'grid-3'];
    } else {
      return showProxyWidget
        ? ['grid-12', 'grid-12', 'grid-12', 'grid-12']
        : ['grid-12', 'grid-12', 'grid-12'];
    }
  };

  return (
    <>
      <ContainerWrapper
        loading={state === DataState.LOADING}
        loaded={state === DataState.LOADED}
        error={state === DataState.ERROR}
      >
        <Row
          width='100%'
          widths={getRowWidths(
            isBig,
            showProxyWidget(isTm, profile, isProxying)
          )}
          alignmentVertical='top'
          wrap='down'
          spacing='none'
        >
          <ConditionalWrap
            condition={isBig}
            wrap={(children: any) => (
              <Column alignmentVertical='top' heights='fill'>
                {children}
              </Column>
            )}
          >
            <UserInfoSelect
              givenName={profile.user.displayName}
              username={profile.user.userName}
              weeks={weeksList()}
              selectedWeek={selectedWeek}
            />
          </ConditionalWrap>
          {showProxyWidget(isTm, profile, isProxying) && (
            <ConditionalWrap
              condition={isBig}
              wrap={(children: any) => (
                <Column alignmentVertical='top' heights='fill'>
                  {children}
                </Column>
              )}
            >
              <ProxyWidget userProfile={profile} />
            </ConditionalWrap>
          )}
          {profile.hasCoachesPermission && (
            <ConditionalWrap
              condition={isBig}
              wrap={(children: any) => (
                <Column alignmentVertical='top' heights='fill'>
                  {children}
                </Column>
              )}
            >
              <Coaches coaches={profile.sharedProfileCoaches} />
            </ConditionalWrap>
          )}

          {profile.hasSharedProfilesPermission && (
            <ConditionalWrap
              condition={isBig}
              wrap={(children: any) => (
                <Column alignmentVertical='top' heights='fill'>
                  {children}
                </Column>
              )}
            >
              <Profiles sharedProfiles={profile.sharedProfileCoachees} />
            </ConditionalWrap>
          )}
        </Row>
      </ContainerWrapper>

      <Row width='100%' spacingInset='400 none' spacing='none'>
        <Divider size='small' />
      </Row>
    </>
  );
};

export default ProfileContainer;
